import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Mail, Phone, MapPin, Building2, Download, ExternalLink, Send, AlertTriangle, Pencil, Save, RefreshCw, X, Plus, Trash2, MailCheck, MailOpen, MousePointerClick, MailX, CheckCircle2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { PreventivoEditor } from "@/pages/PreventivoEditor";
import { api, API, fmtEur, fmtNum, downloadReportPdf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminLeadDetail() {
  const { leadId } = useParams();
  const [searchParams] = useSearchParams();
  const [lead, setLead] = useState(null);
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(false);
  const [reproc, setReproc] = useState(false);
  const [form, setForm] = useState({});
  const [offersCfg, setOffersCfg] = useState({ energy_offers: [], gas_offers: [] });
  const [pick, setPick] = useState({ energy: "", gas: "" });
  const [applying, setApplying] = useState(false);
  const [recalc, setRecalc] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const fetchLead = useCallback(() => api.get(`/admin/leads/${leadId}`).then((r) => { setLead(r.data); setRefreshTick((t) => t + 1); }).catch(() => {}), [leadId]);

  useEffect(() => { fetchLead(); }, [fetchLead]);
  useEffect(() => {
    api.get(`/admin/settings`).then((r) => setOffersCfg({
      energy_offers: r.data.energy_offers || [], gas_offers: r.data.gas_offers || [],
    })).catch(() => {});
  }, []);
  useEffect(() => {
    if (lead) setPick({
      energy: lead.offer_choice?.energy || lead.commercial?.energy_supplier?.fornitore || "",
      gas: lead.offer_choice?.gas || lead.commercial?.gas_supplier?.fornitore || "",
    });
  }, [lead]);

  const applyOfferChoice = async () => {
    setApplying(true);
    try {
      await api.put(`/admin/leads/${leadId}/offer-choice`, { energy: pick.energy, gas: pick.gas });
      toast.info("Offerta aggiornata, rielaborazione in corso…");
      let st = "processing";
      for (let i = 0; i < 45; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const s = await api.get(`/leads/${leadId}/status`);
        st = s.data.status;
        if (st !== "processing") break;
      }
      await fetchLead();
      toast[st === "error" ? "error" : "success"](st === "error" ? "Rielaborazione fallita" : "Offerta applicata e valutazione aggiornata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Operazione fallita");
    } finally {
      setApplying(false);
    }
  };

  const openEdit = () => {
    const e = lead.energy_data || {}, g = lead.gas_data || {}, cs = lead.commercial || {};
    setForm({
      e_consumo: e.consumo_annuo_kwh ?? "", e_materia: e.prezzo_materia_eur_kwh ?? cs.energy_supplier?.prezzo_attuale ?? "",
      e_finito: e.prezzo_finito_eur_kwh ?? e.prezzo_medio_eur_kwh ?? "", e_potenza: e.potenza_impegnata_kw ?? "",
      e_costo: e.costo_totale_eur ?? "",
      e_f1: e.f1_kwh ?? "", e_f2: e.f2_kwh ?? "", e_f3: e.f3_kwh ?? "",
      e_battery: lead.manual_overrides?.energy?.battery_units ?? cs.pv_turnkey?.battery_units ?? "",
      e_premium_battery: lead.manual_overrides?.energy?.premium_battery_units ?? cs.scenarios?.premium?.battery_units ?? "",
      e_trifase: lead.manual_overrides?.energy?.trifase ?? false,
      e_vincolo: lead.manual_overrides?.energy?.vincolo ?? false,
      g_consumo: g.consumo_annuo_smc ?? "", g_materia: g.prezzo_materia_eur_smc ?? cs.gas_supplier?.prezzo_attuale ?? "",
      g_finito: g.prezzo_finito_eur_smc ?? "", g_costo: g.costo_totale_eur ?? "",
      pv_kwp: lead.manual_overrides?.pv?.kwp ?? "",
      pv_surface: lead.manual_overrides?.pv?.surface_m2 ?? "",
      pv_ground: lead.manual_overrides?.pv?.ground_mount ?? (cs.pv_turnkey?.tipo_installazione === "terra" || cs.pv_seu?.tipo_installazione === "terra"),
      pv_bom_enabled: !!cs.pv_bom?.manuale,
      pv_bom: (cs.pv_bom?.righe || []).map((r) => ({
        descrizione: r.descrizione, quantita: r.quantita, unita: r.unita,
        prezzo_unitario: r.prezzo_unitario, margine_pct: r.margine_pct, custom: !!r.custom,
      })),
    });
    setEditing(true);
  };

  const setF = (k) => (ev) => setForm((p) => ({ ...p, [k]: ev.target.value }));

  const setBomRow = (i, k, v) => setForm((p) => {
    const b = [...(p.pv_bom || [])];
    b[i] = { ...b[i], [k]: v };
    return { ...p, pv_bom: b };
  });
  const addBomRow = () => setForm((p) => ({
    ...p,
    pv_bom: [...(p.pv_bom || []), { descrizione: "", quantita: 1, unita: "", prezzo_unitario: 0, margine_pct: 0, custom: true }],
  }));
  const removeBomRow = (i) => setForm((p) => ({ ...p, pv_bom: (p.pv_bom || []).filter((_, j) => j !== i) }));
  const setBomMarginEur = (i) => (e) => setForm((p) => {
    const b = [...(p.pv_bom || [])];
    const row = b[i] || {};
    const dm = (parseFloat(row.quantita) || 0) * (parseFloat(row.prezzo_unitario) || 0);
    const eur = parseFloat(e.target.value);
    const pct = dm > 0 && !isNaN(eur) ? Math.round((eur / dm) * 10000) / 100 : 0;
    b[i] = { ...row, margine_pct: pct };
    return { ...p, pv_bom: b };
  });

  const saveManual = async () => {
    setReproc(true);
    try {
      await api.put(`/admin/leads/${leadId}/manual-data`, {
        energy: {
          consumo_annuo_kwh: form.e_consumo, prezzo_materia_eur_kwh: form.e_materia,
          prezzo_finito_eur_kwh: form.e_finito, potenza_impegnata_kw: form.e_potenza,
          costo_totale_eur: form.e_costo,
          f1_kwh: form.e_f1, f2_kwh: form.e_f2, f3_kwh: form.e_f3,
          battery_units: form.e_battery,
          premium_battery_units: form.e_premium_battery,
          trifase: form.e_trifase,
          vincolo: form.e_vincolo,
        },
        gas: {
          consumo_annuo_smc: form.g_consumo, prezzo_materia_eur_smc: form.g_materia,
          prezzo_finito_eur_smc: form.g_finito, costo_totale_eur: form.g_costo,
        },
        pv: {
          kwp: form.pv_kwp,
          surface_m2: form.pv_surface,
          ground_mount: form.pv_ground,
          bom: form.pv_bom_enabled ? (form.pv_bom || []) : null,
        },
      });
      toast.info("Dati salvati, rielaborazione in corso…");
      let finalStatus = "processing";
      for (let i = 0; i < 45; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const s = await api.get(`/leads/${leadId}/status`);
        finalStatus = s.data.status;
        if (finalStatus !== "processing") break;
      }
      await fetchLead();
      setEditing(false);
      if (finalStatus === "error") {
        toast.error("Rielaborazione terminata con errore, controlla i dati inseriti");
      } else {
        toast.success("Valutazione aggiornata con i nuovi dati");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rielaborazione fallita");
    } finally {
      setReproc(false);
    }
  };

  const recalculate = async () => {
    if (!window.confirm("Ricalcolare questo lead con le regole e i parametri più recenti (fotovoltaico, SEU, offerte)? Verranno usati i dati già estratti dalle bollette.")) return;
    setRecalc(true);
    try {
      await api.post(`/admin/leads/${leadId}/recalculate`);
      toast.info("Ricalcolo in corso…");
      let st = "processing";
      for (let i = 0; i < 45; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const s = await api.get(`/leads/${leadId}/status`);
        st = s.data.status;
        if (st !== "processing") break;
      }
      await fetchLead();
      toast[st === "error" ? "error" : "success"](st === "error" ? "Ricalcolo fallito" : "Lead ricalcolato con i parametri aggiornati");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ricalcolo fallito");
    } finally {
      setRecalc(false);
    }
  };

  const sendEmail = async () => {
    const to = window.prompt("Invia il dossier PDF via email a:", lead?.company?.email || "");
    if (!to) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/leads/${leadId}/send-report`, { email: to });
      toast.success(`Dossier inviato a ${data.recipient}`);
      setLead((p) => ({ ...p, report_sent_to: data.recipient, report_sent_at: new Date().toISOString() }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invio email fallito");
    } finally {
      setSending(false);
    }
  };

  if (!lead) return <div className="min-h-screen flex items-center justify-center text-slate-500">Caricamento…</div>;

  const c = lead.company || {};
  const p = lead.preliminary || {};
  const e = lead.energy_data || {};
  const g = lead.gas_data || {};
  const a = lead.analysis || {};
  const eoi = a.eoi || {};
  const cs = lead.commercial || {};

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 glass">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <Link to="/admin" className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="h-4 w-4" /> Torna ai lead
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-heading font-black text-3xl text-white">{c.ragione_sociale}</h1>
            <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-400">
              <span className="flex items-center gap-1.5"><Mail className="h-4 w-4" /> {c.email}</span>
              <span className="flex items-center gap-1.5"><Phone className="h-4 w-4" /> {c.telefono}</span>
              <span className="flex items-center gap-1.5"><Building2 className="h-4 w-4" /> {c.settore}</span>
              <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {c.provincia}</span>
            </div>
          </div>
          <div className="flex gap-2">
            {lead.status !== "manual" && (
              <Button onClick={recalculate} disabled={recalc} data-testid="recalculate-btn"
                className="h-10 rounded-xl bg-navy-600 hover:bg-navy-500 border border-gold-500/40 text-white font-semibold">
                <RefreshCw className={`h-4 w-4 mr-1.5 ${recalc ? "animate-spin" : ""}`} /> {recalc ? "Ricalcolo…" : "Ricalcola"}
              </Button>
            )}
            <Link to={`/report/${leadId}`}>
              <Button variant="outline" className="h-10 rounded-xl border-white/15 text-slate-200 bg-transparent hover:bg-white/5">
                <ExternalLink className="h-4 w-4 mr-1.5" /> Report
              </Button>
            </Link>
            {a.report && (
              <Button onClick={() => downloadReportPdf(leadId, `Audit_${(c.ragione_sociale||'azienda').replace(/\s+/g,'_')}.pdf`).catch(() => window.open(`${API}/leads/${leadId}/report/pdf`, "_blank"))}
                className="h-10 rounded-xl bg-gold-500 hover:bg-gold-400 text-[#0A1A2F] font-semibold">
                <Download className="h-4 w-4 mr-1.5" /> PDF
              </Button>
            )}
            {a.report && (
              <Button onClick={sendEmail} disabled={sending} data-testid="send-report-email-btn"
                className="h-10 rounded-xl bg-navy-600 hover:bg-navy-500 border border-gold-500/40 text-white font-semibold">
                <Send className="h-4 w-4 mr-1.5" /> {sending ? "Invio…" : "Invia email"}
              </Button>
            )}
          </div>
        </div>

        {((e.duplicati_rimossi || g.duplicati_rimossi || 0) > 0 || cs.prezzo_riferimento_stimato) && (
          <div className="rounded-xl border border-blue-500/30 bg-blue-500/10 p-4 flex items-start gap-3" data-testid="excel-ingest-note">
            <AlertTriangle className="h-5 w-5 text-blue-300 shrink-0 mt-0.5" />
            <div>
              <p className="text-blue-100 font-semibold text-sm">Dati importati da file — verifica consigliata</p>
              <ul className="text-blue-100/80 text-xs mt-1 space-y-0.5 list-disc pl-4">
                {(e.duplicati_rimossi || g.duplicati_rimossi || 0) > 0 && (
                  <li>{(e.duplicati_rimossi || 0) + (g.duplicati_rimossi || 0)} contatore/i duplicato/i (stesso POD/PDR presente sia in fattura sia nel file Excel) non conteggiato/i due volte.</li>
                )}
                {cs.prezzo_riferimento_stimato && (
                  <li>Il file non riportava i costi: il prezzo di riferimento €/kWh–€/Smc è stato <b>stimato</b> dai valori di mercato (modificabili in Impostazioni). Correggi con i prezzi reali per risultati precisi.</li>
                )}
              </ul>
            </div>
          </div>
        )}

        {cs.pv_turnkey?.ground_breakdown && (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.07] p-4" data-testid="ground-breakdown">
            <p className="text-emerald-200 font-semibold text-sm mb-2">Impianto a terra / agrivoltaico — dettaglio costi DM (solo interno) · {cs.pv_turnkey.kwp} kWp</p>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1 text-xs text-emerald-100/80">
              {Object.entries(cs.pv_turnkey.ground_breakdown.breakdown_costi || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-white/5 py-0.5">
                  <span className="capitalize">{k.replace(/_/g, " ")}</span>
                  <span className="font-mono-plex">€ {Number(v).toLocaleString("it-IT")}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 grid sm:grid-cols-3 gap-3 text-xs">
              <div className="rounded-lg bg-white/5 p-2">
                <div className="text-slate-400">Costo totale DM</div>
                <div className="text-white font-semibold">€ {Number(cs.pv_turnkey.ground_breakdown.costo_totale_dm).toLocaleString("it-IT")}</div>
              </div>
              <div className="rounded-lg bg-white/5 p-2">
                <div className="text-slate-400">Impianto +{cs.pv_turnkey.ground_breakdown.margine_impianto_pct}% / Resto +{cs.pv_turnkey.ground_breakdown.margine_resto_pct}%</div>
                <div className="text-white font-semibold">€ {Number(cs.pv_turnkey.ground_breakdown.prezzo_impianto).toLocaleString("it-IT")} + € {Number(cs.pv_turnkey.ground_breakdown.prezzo_resto).toLocaleString("it-IT")}</div>
              </div>
              <div className="rounded-lg bg-gold-500/10 border border-gold-500/30 p-2">
                <div className="text-gold-300/80">Prezzo cliente</div>
                <div className="text-gold-200 font-bold">€ {Number(cs.pv_turnkey.investimento).toLocaleString("it-IT")}</div>
              </div>
            </div>
          </div>
        )}


        {(e.consumo_corretto || g.consumo_corretto) && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 flex items-start gap-3" data-testid="consumo-corretto-warning">
            <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-200 font-semibold text-sm">Consumo auto-corretto — verifica manuale consigliata</p>
              <p className="text-amber-100/80 text-xs mt-1">
                Il consumo di {(e.num_bollette_corrette || 0) + (g.num_bollette_corrette || 0)} bolletta/e risultava incoerente con il prezzo unitario stampato ed è stato ricalcolato automaticamente (consumo = imponibile ÷ prezzo unitario). Controlla i kWh/Smc estratti prima di inviare il report al cliente.
              </p>
            </div>
          </div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Energy Score" value={p.efficiency_score ?? "–"} />
          <Kpi label="Risparmio/anno" value={p.savings_potential_eur_year ? fmtEur(p.savings_potential_eur_year) : "–"} />
          <Kpi label="EOI (10 anni)" value={eoi.economic_opportunity_10y_eur ? fmtEur(eoi.economic_opportunity_10y_eur) : "–"} accent />
          <Kpi label="Priorità" value={eoi.overall_priority != null ? `${fmtNum(eoi.overall_priority, 1)}/10` : "–"} />
        </div>

        {(lead.urgency || lead.priority_map) && (() => {
          const u = lead.urgency || {};
          const pm = lead.priority_map || {};
          const band = u.band || "cold";
          const bandCfg = { hot: { c: "text-red-400", b: "border-red-500/40 bg-red-500/10", l: "CALDO" }, warm: { c: "text-amber-400", b: "border-amber-500/40 bg-amber-500/10", l: "TIEPIDO" }, cold: { c: "text-blue-400", b: "border-blue-500/40 bg-blue-500/10", l: "FREDDO" } }[band];
          const hLabel = { immediato: "Immediato", "6_mesi": "Entro 6 mesi", "12_mesi": "Entro 12 mesi", esplorativo: "Solo esplorativo" }[pm.horizon] || "—";
          const pLabels = { costi: "Abbattimento costi", indipendenza: "Indipendenza energetica", sostenibilita: "Sostenibilità/ESG", protezione: "Protezione rincari", iperammortamento: "Iperammortamento" };
          return (
            <div className={`rounded-2xl p-5 border ${bandCfg.b} flex flex-col md:flex-row md:items-center gap-5`} data-testid="urgency-panel">
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="font-heading font-black text-4xl text-white leading-none" data-testid="urgency-score">{u.score ?? "–"}</div>
                  <div className="text-[10px] text-slate-400 tracking-wider mt-1">/ 100</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Urgency Score</div>
                  <div className={`font-bold text-lg ${bandCfg.c}`} data-testid="urgency-band">{bandCfg.l}</div>
                </div>
              </div>
              <div className="md:border-l md:border-white/10 md:pl-5 flex-1">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2">Preferenze del cliente</div>
                <div className="flex flex-wrap gap-2 items-center text-sm">
                  <span className="px-2.5 h-7 inline-flex items-center rounded-full bg-white/5 border border-white/10 text-slate-200">Orizzonte: <b className="ml-1 text-gold-300">{hLabel}</b></span>
                  {(pm.priorities || []).length ? (pm.priorities.map((p) => (
                    <span key={p} className="px-2.5 h-7 inline-flex items-center rounded-full bg-gold-500/10 border border-gold-500/30 text-gold-200">{pLabels[p] || p}</span>
                  ))) : <span className="text-slate-500 text-xs">Nessuna priorità indicata</span>}
                  {pm.budget && <span className="px-2.5 h-7 inline-flex items-center rounded-full bg-white/5 border border-white/10 text-slate-300">Budget: {pm.budget}</span>}
                </div>
              </div>
            </div>
          );
        })()}

        {lead.email_tracking && (() => {
          const t = lead.email_tracking;
          const fmt = (s) => { try { return new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return null; } };
          const clicked = t.click_count > 0 || t.status === "clicked";
          const rv = lead.report_view || {};
          const viewed = (rv.count || 0) > 0;   // apertura reale della pagina report (server-side, affidabile)
          const opened = t.open_count > 0 || !!t.opened_at || clicked || viewed;
          const delivered = !!t.delivered_at || ["delivered", "opened", "clicked"].includes(t.status) || viewed;
          const isBounce = t.status === "bounced" || t.status === "complained";
          const steps = [
            { key: "recv", label: "Ricevuta dal cliente", done: delivered, when: t.delivered_at, Icon: MailCheck, extra: null },
            { key: "open", label: "Aperta / letta", done: opened, when: t.last_opened_at || t.opened_at, Icon: MailOpen, extra: t.open_count > 0 ? `${t.open_count} aperture` : (clicked || viewed ? "confermata" : null) },
            { key: "click", label: "Ha aperto il report", done: clicked || viewed, when: rv.last_at || t.clicked_at, Icon: MousePointerClick, extra: viewed ? `${rv.count} apertur${rv.count > 1 ? "e" : "a"} online` : (t.click_count > 0 ? `${t.click_count} click` : null) },
          ];
          return (
            <div className="rounded-2xl p-5 border border-white/10 bg-white/5" data-testid="email-tracking-panel">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gold-400" />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Dossier via email</span>
                </div>
                <span className="text-sm text-slate-300">A: <b className="text-slate-100">{t.recipient}</b></span>
              </div>
              {isBounce ? (
                <div className="flex items-center gap-2 text-red-300 font-semibold" data-testid="email-tracking-status">
                  <MailX className="h-5 w-5" /> {t.status === "bounced" ? "Email NON recapitata (indirizzo errato/casella piena)" : "Segnalata come spam dal destinatario"}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="email-tracking-status">
                  {steps.map((s) => {
                    const S = s.Icon;
                    return (
                      <div key={s.key} data-testid={`email-step-${s.key}`}
                        className={`rounded-xl border p-3 flex items-start gap-3 ${s.done ? "border-emerald-500/40 bg-emerald-500/10" : "border-white/10 bg-white/[0.02]"}`}>
                        <div className={`mt-0.5 h-8 w-8 rounded-lg flex items-center justify-center ${s.done ? "bg-emerald-500/20 text-emerald-300" : "bg-white/5 text-slate-500"}`}>
                          {s.done ? <CheckCircle2 className="h-4.5 w-4.5" /> : <S className="h-4.5 w-4.5" />}
                        </div>
                        <div className="min-w-0">
                          <div className={`text-sm font-semibold ${s.done ? "text-emerald-200" : "text-slate-400"}`}>{s.label}</div>
                          {s.done ? (
                            <div className="text-[11px] text-emerald-300/70">{s.when ? fmt(s.when) : "sì"}{s.extra ? ` · ${s.extra}` : ""}</div>
                          ) : (
                            <div className="text-[11px] text-slate-500">in attesa</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {t.status === "sent" && !delivered && (
                <div className="text-slate-500 text-xs mt-3">Inviata il {fmt(t.sent_at)} · in attesa di conferma di consegna</div>
              )}
              <div className="text-[10px] text-slate-500 mt-3">Nota: l'apertura via email è rilevata via pixel (alcuni client la bloccano). L'apertura del report online è invece registrata direttamente dal nostro sistema ed è sempre affidabile.</div>
            </div>
          );
        })()}

        <div className="grid md:grid-cols-2 gap-6">
          <Block title="Dati energia elettrica" data={e} />
          <Block title="Dati gas" data={g} />
        </div>

        <div className="card-dark rounded-2xl p-6" data-testid="manual-data-editor">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-heading font-bold text-lg text-white">Correzione manuale dati fattura</h3>
            {!editing ? (
              <Button onClick={openEdit} size="sm" className="h-9 rounded-xl bg-navy-600 hover:bg-navy-500 border border-gold-500/40 text-white" data-testid="edit-data-btn">
                <Pencil className="h-4 w-4 mr-1.5" /> Modifica dati
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button onClick={() => setEditing(false)} disabled={reproc} size="sm" variant="outline" className="h-9 rounded-xl border-white/15 bg-transparent text-slate-300" data-testid="cancel-edit-btn">
                  <X className="h-4 w-4 mr-1" /> Annulla
                </Button>
                <Button onClick={saveManual} disabled={reproc} size="sm" className="h-9 rounded-xl bg-gold-500 hover:bg-gold-400 text-[#0A1A2F] font-semibold" data-testid="save-reprocess-btn">
                  {reproc ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
                  {reproc ? "Rielaborazione…" : "Salva e rielabora"}
                </Button>
              </div>
            )}
          </div>
          <p className="text-slate-500 text-xs mb-4">Se il consumo reale delle fatture non è stato recepito correttamente, correggilo qui: il sistema rielabora automaticamente score, confronto fornitori e proposte fotovoltaiche con i nuovi dati.</p>

          {editing ? (
            <>
            <div className="grid md:grid-cols-2 gap-6" data-testid="manual-edit-form">
              <div className="space-y-3">
                <p className="text-gold-400 text-xs font-semibold uppercase tracking-wider">Energia elettrica</p>
                <EditField label="Consumo annuo (kWh)" value={form.e_consumo} onChange={setF("e_consumo")} testid="e-consumo" />
                <div className="grid grid-cols-3 gap-2">
                  <EditField label="Fascia F1 (kWh)" value={form.e_f1} onChange={setF("e_f1")} testid="e-f1" />
                  <EditField label="Fascia F2 (kWh)" value={form.e_f2} onChange={setF("e_f2")} testid="e-f2" />
                  <EditField label="Fascia F3 (kWh)" value={form.e_f3} onChange={setF("e_f3")} testid="e-f3" />
                </div>
                {(() => {
                  const sum = (parseFloat(form.e_f1) || 0) + (parseFloat(form.e_f2) || 0) + (parseFloat(form.e_f3) || 0);
                  const tot = parseFloat(form.e_consumo) || 0;
                  if (sum > 0 && tot > 0 && Math.abs(sum - tot) > 0.01 * tot) {
                    return (
                      <div className="flex items-start gap-2 rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-2" data-testid="fasce-mismatch-warning">
                        <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                        <span className="text-amber-200 text-xs">
                          La somma delle fasce (F1+F2+F3 = {fmtNum(sum)} kWh) non coincide con il consumo annuo inserito ({fmtNum(tot)} kWh). Differenza {fmtNum(Math.abs(sum - tot))} kWh — verifica i valori prima di salvare.
                        </span>
                      </div>
                    );
                  }
                  return null;
                })()}
                <EditField label="Prezzo materia energia (€/kWh)" value={form.e_materia} onChange={setF("e_materia")} testid="e-materia" step="0.001" />
                <EditField label="Prezzo finito omnicompr. (€/kWh)" value={form.e_finito} onChange={setF("e_finito")} testid="e-finito" step="0.001" />
                <EditField label="Potenza impegnata (kW)" value={form.e_potenza} onChange={setF("e_potenza")} testid="e-potenza" step="0.1" />
                <EditField label="Costo totale annuo (€)" value={form.e_costo} onChange={setF("e_costo")} testid="e-costo" />
                <EditField label="Batterie 5 kW — n° unità (se richieste)" value={form.e_battery} onChange={setF("e_battery")} testid="e-battery" step="1" />
                <div className="flex flex-wrap gap-4 pt-1">
                  <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer" data-testid="e-trifase-label">
                    <input type="checkbox" checked={!!form.e_trifase} onChange={(ev) => setForm((p) => ({ ...p, e_trifase: ev.target.checked }))} data-testid="e-trifase" className="h-4 w-4 rounded border-slate-500 bg-navy-800 text-gold-500" />
                    Impianto trifase (+ maggiorazione)
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer" data-testid="e-vincolo-label">
                    <input type="checkbox" checked={!!form.e_vincolo} onChange={(ev) => setForm((p) => ({ ...p, e_vincolo: ev.target.checked }))} data-testid="e-vincolo" className="h-4 w-4 rounded border-slate-500 bg-navy-800 text-gold-500" />
                    Vincolo paesaggistico (pratica dedicata)
                  </label>
                </div>
              </div>
              <div className="space-y-3">
                <p className="text-gold-400 text-xs font-semibold uppercase tracking-wider">Gas naturale</p>
                <EditField label="Consumo annuo (Smc)" value={form.g_consumo} onChange={setF("g_consumo")} testid="g-consumo" />
                <EditField label="Prezzo materia gas (€/Smc)" value={form.g_materia} onChange={setF("g_materia")} testid="g-materia" step="0.001" />
                <EditField label="Prezzo finito (€/Smc)" value={form.g_finito} onChange={setF("g_finito")} testid="g-finito" step="0.001" />
                <EditField label="Costo totale annuo (€)" value={form.g_costo} onChange={setF("g_costo")} testid="g-costo" />
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-white/10 space-y-3" data-testid="manual-pv-form">
              <p className="text-gold-400 text-xs font-semibold uppercase tracking-wider">Impianto fotovoltaico (dimensionamento)</p>
              <div className="grid md:grid-cols-3 gap-4">
                <EditField label="Potenza impianto (kWp)" value={form.pv_kwp} onChange={setF("pv_kwp")} testid="pv-kwp" step="0.1"
                  placeholder={cs.pv_seu?.kwp || cs.pv_turnkey?.kwp ? `auto: ${fmtNum(cs.pv_seu?.kwp || cs.pv_turnkey?.kwp)} kWp` : "auto"} />
                <EditField label="Superficie disponibile (m²)" value={form.pv_surface} onChange={setF("pv_surface")} testid="pv-surface" step="1"
                  placeholder={cs.pv_turnkey?.superficie_m2 ? `auto: ${fmtNum(cs.pv_turnkey?.superficie_m2)} m²` : "auto"} />
                <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer md:pb-2 md:self-end" data-testid="pv-ground-label">
                  <input type="checkbox" checked={!!form.pv_ground} onChange={(ev) => setForm((p) => ({ ...p, pv_ground: ev.target.checked }))} data-testid="pv-ground" className="h-4 w-4 rounded border-slate-500 bg-navy-800 text-gold-500" />
                  Installazione a terra (agrivoltaico)
                </label>
              </div>
              <p className="text-slate-500 text-xs">La potenza inserita ha <b className="text-slate-400">priorità</b> sul dimensionamento automatico. Lasciando vuoto il campo, l'impianto viene dimensionato sulla superficie o sui consumi. L'opzione "a terra" attiva il modello Ground-mount/agrivoltaico.</p>

              <div className="mt-4 grid md:grid-cols-2 gap-4 items-end" data-testid="premium-battery-form">
                <EditField label="Accumulo Scenario Premium — n° batterie (5 kW cad.)" value={form.e_premium_battery} onChange={setF("e_premium_battery")} testid="e-premium-battery" step="1"
                  placeholder={cs.scenarios?.premium?.battery_units_consigliato ? `auto: ${cs.scenarios.premium.battery_units_consigliato} unità (${cs.scenarios.premium.accumulo_consigliato_kwh} kWh)` : "auto sui consumi notturni"} />
                <p className="text-slate-500 text-xs pb-2">L'accumulo Premium è calcolato automaticamente sul consumo non coperto dall'autoproduzione (serale/notturno). Inserisci un valore per <b className="text-slate-400">forzarlo manualmente</b>; lascia vuoto per il valore consigliato.</p>
              </div>

              <div className="mt-5 pt-5 border-t border-white/10" data-testid="pv-bom-editor">
                <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer mb-1" data-testid="pv-bom-enable-label">
                  <input type="checkbox" checked={!!form.pv_bom_enabled} onChange={(ev) => setForm((p) => ({ ...p, pv_bom_enabled: ev.target.checked }))} data-testid="pv-bom-enable" className="h-4 w-4 rounded border-slate-500 bg-navy-800 text-gold-500" />
                  <span className="font-semibold">Personalizza distinta materiali + installazione</span>
                </label>
                <p className="text-slate-500 text-xs mb-3">I prezzi sono <b className="text-slate-400">costi DM</b> (il sistema applica il margine %). Se attiva, la somma delle voci sostituisce l'investimento calcolato automaticamente. Puoi aggiungere o rimuovere voci (es. trasporto, recinzione).</p>
                {form.pv_bom_enabled && (
                  <div className="space-y-2">
                    <div className="hidden md:grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-slate-500 px-1">
                      <span className="col-span-3">Descrizione</span>
                      <span className="col-span-2 text-right">Quantità</span>
                      <span className="col-span-2 text-right">Prezzo unit. DM (€)</span>
                      <span className="col-span-1 text-right">Marg.%</span>
                      <span className="col-span-2 text-right">Margine €</span>
                      <span className="col-span-1 text-right">Tot.</span>
                      <span className="col-span-1" />
                    </div>
                    {(form.pv_bom || []).map((r, i) => {
                      const dm = (parseFloat(r.quantita) || 0) * (parseFloat(r.prezzo_unitario) || 0);
                      const tot = dm * (1 + (parseFloat(r.margine_pct) || 0) / 100);
                      const marg = Math.round(tot - dm);
                      return (
                        <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`pv-bom-row-${i}`}>
                          <Input value={r.descrizione ?? ""} onChange={(e) => setBomRow(i, "descrizione", e.target.value)} className="input-dark h-9 col-span-12 md:col-span-3" placeholder="Voce" data-testid={`pv-bom-desc-${i}`} />
                          <Input type="number" step="0.01" value={r.quantita ?? ""} onChange={(e) => setBomRow(i, "quantita", e.target.value)} className="input-dark h-9 col-span-4 md:col-span-2 text-right" data-testid={`pv-bom-qta-${i}`} />
                          <Input type="number" step="0.01" value={r.prezzo_unitario ?? ""} onChange={(e) => setBomRow(i, "prezzo_unitario", e.target.value)} className="input-dark h-9 col-span-4 md:col-span-2 text-right" data-testid={`pv-bom-prezzo-${i}`} />
                          <Input type="number" step="1" value={r.margine_pct ?? ""} onChange={(e) => setBomRow(i, "margine_pct", e.target.value)} className="input-dark h-9 col-span-2 md:col-span-1 text-right" data-testid={`pv-bom-margine-${i}`} />
                          <Input type="number" step="1" value={marg} onChange={setBomMarginEur(i)} className="input-dark h-9 col-span-3 md:col-span-2 text-right text-emerald-300" data-testid={`pv-bom-margeur-${i}`} />
                          <span className="col-span-2 md:col-span-1 text-right text-slate-200 font-mono-plex text-xs">{fmtEur(tot)}</span>
                          <button type="button" onClick={() => removeBomRow(i)} className="col-span-1 text-slate-500 hover:text-red-400 flex justify-end" data-testid={`pv-bom-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                        </div>
                      );
                    })}
                    <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                      <Button type="button" onClick={addBomRow} size="sm" variant="outline" className="h-8 rounded-lg border-gold-500/40 text-gold-300 hover:bg-gold-500/10" data-testid="pv-bom-add"><Plus className="h-4 w-4 mr-1" /> Aggiungi voce</Button>
                      <div className="text-right text-sm space-x-3">
                        <span className="text-slate-500">Costo DM: <span className="font-mono-plex text-slate-300">{fmtEur((form.pv_bom || []).reduce((a, r) => a + (parseFloat(r.quantita) || 0) * (parseFloat(r.prezzo_unitario) || 0), 0))}</span></span>
                        <span className="text-emerald-400/90">Margine: <span className="font-mono-plex font-semibold" data-testid="pv-bom-margin-total">{fmtEur((form.pv_bom || []).reduce((a, r) => a + (parseFloat(r.quantita) || 0) * (parseFloat(r.prezzo_unitario) || 0) * ((parseFloat(r.margine_pct) || 0) / 100), 0))}</span></span>
                        <span className="text-slate-400">Investimento cliente: <span className="font-mono-plex text-gold-400 font-semibold" data-testid="pv-bom-total">{fmtEur((form.pv_bom || []).reduce((a, r) => a + (parseFloat(r.quantita) || 0) * (parseFloat(r.prezzo_unitario) || 0) * (1 + (parseFloat(r.margine_pct) || 0) / 100), 0))}</span></span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            </>
          ) : (
            e.dati_corretti_manualmente && (
              <p className="text-emerald-300/90 text-xs" data-testid="manual-edited-flag">✓ I dati di questo lead sono stati corretti manualmente.</p>
            )
          )}
        </div>

        <PreventivoEditor key={refreshTick} leadId={leadId} autoOpen={searchParams.get("preventivo") === "1"} />

        {(cs.energy_supplier || cs.gas_supplier) && (
          <div className="card-dark rounded-2xl p-6" data-testid="selected-offers-block">
            <h3 className="font-heading font-bold text-lg text-white mb-1">Offerte selezionate (interno)</h3>
            <p className="text-slate-500 text-xs mb-4">Migliore offerta scelta automaticamente per questo lead. Puoi selezionarne una diversa qui sotto e rigenerare la valutazione. Il nome fornitore NON compare nel report del cliente.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {cs.energy_supplier && (
                <div className="rounded-xl border border-white/10 p-4" data-testid="selected-energy-offer">
                  <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Energia elettrica {cs.settings_used?.energy_offer_manuale && <span className="text-amber-300">(scelta manuale)</span>}</p>
                  <p className="text-white font-heading font-bold text-lg">{cs.energy_supplier.fornitore || "Offerta CTE DM"}</p>
                  <p className="text-gold-400 font-mono-plex mt-1">{fmtNum(cs.energy_supplier.prezzo_dm, 3)} €/kWh</p>
                  <p className="text-slate-400 text-xs mt-1">Attuale {fmtNum(cs.energy_supplier.prezzo_attuale, 3)} €/kWh · Risparmio {fmtEur(cs.energy_supplier.risparmio_anno)}/anno ({fmtNum(cs.energy_supplier.risparmio_pct, 1)}%)</p>
                </div>
              )}
              {cs.gas_supplier && (
                <div className="rounded-xl border border-white/10 p-4" data-testid="selected-gas-offer">
                  <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Gas naturale {cs.settings_used?.gas_offer_manuale && <span className="text-amber-300">(scelta manuale)</span>}</p>
                  <p className="text-white font-heading font-bold text-lg">{cs.gas_supplier.fornitore || "Offerta CTE DM"}</p>
                  <p className="text-gold-400 font-mono-plex mt-1">{fmtNum(cs.gas_supplier.prezzo_dm, 3)} €/Smc</p>
                  <p className="text-slate-400 text-xs mt-1">Attuale {fmtNum(cs.gas_supplier.prezzo_attuale, 3)} €/Smc · Risparmio {fmtEur(cs.gas_supplier.risparmio_anno)}/anno ({fmtNum(cs.gas_supplier.risparmio_pct, 1)}%)</p>
                </div>
              )}
            </div>

            <div className="mt-5 pt-4 border-t border-white/10">
              <p className="text-slate-300 text-sm font-semibold mb-3">Cambia offerta manualmente</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-slate-400 text-xs mb-1 block">Offerta energia</label>
                  <select value={pick.energy} onChange={(e) => setPick((p) => ({ ...p, energy: e.target.value }))}
                    className="w-full h-10 rounded-lg bg-navy-800 border border-white/15 text-slate-100 text-sm px-2" data-testid="energy-offer-select">
                    <option value="">— Migliore automatica (idonea al consumo) —</option>
                    {offersCfg.energy_offers.map((o, i) => {
                      const inelig = o.max_kwh && (e.consumo_annuo_kwh || 0) > o.max_kwh;
                      return (
                        <option key={i} value={o.fornitore}>
                          {inelig ? "⚠ " : ""}{o.fornitore} · {fmtNum(o.prezzo_eur_kwh, 3)} €/kWh{o.max_kwh ? ` (≤${o.max_kwh} kWh${inelig ? " — non idonea" : ""})` : ""}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div>
                  <label className="text-slate-400 text-xs mb-1 block">Offerta gas</label>
                  <select value={pick.gas} onChange={(e) => setPick((p) => ({ ...p, gas: e.target.value }))}
                    className="w-full h-10 rounded-lg bg-navy-800 border border-white/15 text-slate-100 text-sm px-2" data-testid="gas-offer-select">
                    <option value="">— Migliore automatica (idonea al consumo) —</option>
                    {offersCfg.gas_offers.map((o, i) => {
                      const inelig = o.max_smc && (g.consumo_annuo_smc || 0) > o.max_smc;
                      return (
                        <option key={i} value={o.fornitore}>
                          {inelig ? "⚠ " : ""}{o.fornitore} · {fmtNum(o.prezzo_eur_smc, 3)} €/Smc{o.max_smc ? ` (≤${o.max_smc} Smc${inelig ? " — non idonea" : ""})` : ""}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
              <Button onClick={applyOfferChoice} disabled={applying} className="mt-4 h-10 rounded-xl bg-gold-500 hover:bg-gold-400 text-[#0A1A2F] font-semibold" data-testid="apply-offer-choice-btn">
                {applying ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
                {applying ? "Rielaborazione…" : "Applica offerta e rigenera"}
              </Button>
            </div>
          </div>
        )}

        {lead.audit_answers?.length > 0 && (
          <div className="card-dark rounded-2xl p-6">
            <h3 className="font-heading font-bold text-lg text-white mb-4">Risposte audit</h3>
            <div className="space-y-3">
              {lead.audit_answers.map((qa, i) => (
                <div key={i} className="border-b border-white/5 pb-3">
                  <p className="text-slate-400 text-sm">{qa.question}</p>
                  <p className="text-white text-sm mt-0.5">{qa.answer}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const Kpi = ({ label, value, accent }) => (
  <div className="card-dark rounded-2xl p-5">
    <p className="text-slate-400 text-xs uppercase tracking-wider">{label}</p>
    <p className={`font-heading font-bold text-2xl mt-1 ${accent ? "text-gold-400" : "text-white"}`}>{value}</p>
  </div>
);

const EditField = ({ label, value, onChange, testid, step = "1", placeholder }) => (
  <div>
    <Label className="text-slate-400 text-xs mb-1 block">{label}</Label>
    <Input type="number" step={step} value={value ?? ""} onChange={onChange} placeholder={placeholder}
      className="input-dark h-10" data-testid={`edit-${testid}`} />
  </div>
);

const Block = ({ title, data }) => {
  const entries = Object.entries(data || {}).filter(([, v]) => v !== null && v !== undefined && v !== "");
  return (
    <div className="card-dark rounded-2xl p-6">
      <h3 className="font-heading font-bold text-lg text-white mb-4">{title}</h3>
      {entries.length === 0 ? (
        <p className="text-slate-600 text-sm">Nessun dato disponibile.</p>
      ) : (
        <div className="space-y-2">
          {entries.map(([k, v]) => (
            <div key={k} className="flex justify-between text-sm border-b border-white/5 pb-1.5">
              <span className="text-slate-500">{k}</span>
              <span className="text-slate-200 font-mono-plex text-right">{String(v)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
